import "./date-modal.scss";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { CrossIcon } from "../../../../../assets/svg";
import { P2Alert, P2Button, P2DateSelector } from "react-bootstrap-common-script";
import { SharedValidators } from "../../../../../utils/validators/shared/validators";
import moment from "moment";
import { ModalBackground } from "../../../shared/modal-background";
import { useAppSelector } from "../../../../../redux/hook";
import { useSolicitorCaseDetailsContext } from "../../../../../context/solicitor-case-details-context";
import { ScdDto } from "../../../../../models/classes/solicitor/Scd.class";
import { PolymerAxios } from "../../../../../utils/axios";
import { API_Solicitor_SaveCompletionDate } from "../../../../../utils/api";

interface DateModalHandle {
  getIsShowing: () => boolean;
  setIsShowing: (value: boolean) => void;
}

const DateModal = forwardRef<DateModalHandle, {}>(({}, ref) => {
  const queryParameters = new URLSearchParams(window.location.search);
  const applicationUuid = queryParameters.get("applicationUuid");

  // States
  const loginUser = useAppSelector((state) => state.auth.AuthenticationSlice.loginUser);
  const singpassToken = useAppSelector((state) => state.auth.AuthenticationSlice.singpassToken);
  const { uen } = useAppSelector((state) => state.auth.AuthenticationSlice.corpPassDetails);

  const { solicitorCaseSummaryData, solcitorCaseSummaryRefetch } = useSolicitorCaseDetailsContext();

  const [isShowing, setIsShowing] = useState<boolean>(false);
  const [newDate, setNewDate] = useState<string>(solicitorCaseSummaryData?.scheduled_completion_date ?? "");
  const [isScdApiError, setIsScdApiError] = useState<boolean>(false);

  const modalContainerRef = useRef<HTMLDivElement>(null);

  // Derived values
  const { isValid, message } = SharedValidators.RequiredDate(newDate);

  const postScheduledCompletionDate = async () => {
    let payload: ScdDto = {
      application_uuid: applicationUuid ?? "",
      scd: newDate,
    };

    try {
      const response = await PolymerAxios(loginUser?.uinfin ?? "", singpassToken, uen).post(`${API_Solicitor_SaveCompletionDate}`, payload);

      if (response.status === 200) {
        solcitorCaseSummaryRefetch();
        setIsScdApiError(false);
        setIsShowing(false);
      } else {
        setIsScdApiError(true);
      }
    } catch (e) {
      setIsScdApiError(true);
    }
  };

  // Functions
  function cancel() {
    setNewDate(solicitorCaseSummaryData?.scheduled_completion_date ?? "");
    setIsScdApiError(false);
    setIsShowing(false);
  }

  useImperativeHandle(
    ref,
    () => {
      return {
        getIsShowing: () => isShowing,
        setIsShowing: (value: boolean) => {
          setIsShowing(value);
        },
      };
    },
    [isShowing]
  );

  // Effects
  useEffect(() => {
    if (solicitorCaseSummaryData?.scheduled_completion_date && !newDate) setNewDate(solicitorCaseSummaryData.scheduled_completion_date);
  }, [solicitorCaseSummaryData]);

  return isShowing ? (
    <ModalBackground innerComponentRef={modalContainerRef} callback={cancel}>
      <div ref={modalContainerRef} className="polymer-date-modal__container">
        <div className="polymer-date-modal__header-container">
          <h2 className="polymer-date-modal__title">Change completion appointment date</h2>
          <button onClick={cancel}>
            <CrossIcon fillColor="#0C8188" />
          </button>
        </div>

        <p className="polymer-date-modal__text">
          You may update the completion appointment date when necessary. Changing the completion appointment date will affect the financial information related
          to the case.
        </p>

        <div style={{ marginBottom: 24 }}>
          <P2DateSelector
            placeholder="DD/MM/YYYY"
            isError={!isValid}
            errorMsg={message}
            value={newDate}
            required
            onChange={(value: string) => setNewDate(value)}
          />
        </div>

        {isScdApiError ? (
          <div style={{ marginBottom: 24 }}>
            <P2Alert variant="danger" withBorder={true} text="An error occurred while saving. Please try again." />
          </div>
        ) : null}

        <div className="polymer-date-modal__button-container">
          <P2Button onClick={cancel} variant="outline-primary">
            Cancel
          </P2Button>
          <P2Button disabled={!isValid} onClick={postScheduledCompletionDate} variant="primary-1">
            Confirm
          </P2Button>
        </div>
      </div>
    </ModalBackground>
  ) : null;
});

export { DateModal, type DateModalHandle };
